import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRSVPForm, useWishesForm } from '../hooks/useRSVPForm.ts';
import { COLORS } from '../constants/colors.ts';
import { RSVP_DEADLINE, RSVP_CLOSED, WISHES_CLOSED, ORGANIZATION_OPTIONS, INVITATION_FROM_OPTIONS } from '../constants/event.ts';

interface RSVPSectionProps {
  rsvpSectionRef: React.RefObject<HTMLElement | null>;
  onWishSubmitted?: () => void;
}

const PAX_OPTIONS = Array.from({ length: 6 }, (_, i) => i + 1);

const SelectArrow: React.FC = () => (
  <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 px-2" style={{ color: COLORS.accent }}>
    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
    </svg>
  </div>
);

const RSVPSection: React.FC<RSVPSectionProps> = ({ rsvpSectionRef, onWishSubmitted }) => {
  const [activeTab, setActiveTab] = useState<'rsvp' | 'wishes'>('rsvp');

  const {
    formData, setFormData,
    isSubmitting: rsvpSubmitting,
    submitError: rsvpError,
    isSubmitted: rsvpSubmitted,
    handleSubmit,
  } = useRSVPForm();

  const {
    wishesData, setWishesData,
    isSubmitting: wishesSubmitting,
    submitError: wishesError,
    isSubmitted: wishesSubmitted,
    handleWishesSubmit,
  } = useWishesForm();

  // Notify parent when RSVP with a wish is submitted
  useEffect(() => {
    if (rsvpSubmitted && onWishSubmitted) {
      onWishSubmitted();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rsvpSubmitted]);

  // Notify parent when a standalone wish is submitted
  useEffect(() => {
    if (wishesSubmitted && onWishSubmitted) {
      onWishSubmitted();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wishesSubmitted]);

  const borderDefault = `${COLORS.accent}55`;

  const inputClass = "w-full px-4 py-3.5 text-base bg-transparent border-b-2 transition-all duration-200 outline-none placeholder:opacity-60";
  const selectClass = "w-full px-4 py-3.5 text-base border-b-2 transition-all duration-200 appearance-none cursor-pointer outline-none";

  const formatPhoneNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 8 && cleaned.length <= 11 && !cleaned.startsWith('60')) {
      return '+6' + cleaned;
    }
    if (cleaned.length > 0 && !value.startsWith('+')) {
      return '+' + cleaned;
    }
    return value;
  };

  const SubmitButton: React.FC<{ isSubmitting: boolean; label: string; loadingLabel: string }> = ({
    isSubmitting, label, loadingLabel
  }) => (
    <button
      type="submit"
      disabled={isSubmitting}
      className="w-full font-optima font-medium py-3.5 rounded-full transition-all duration-300 tracking-[0.15em] uppercase border-2 disabled:opacity-60 active:scale-[0.98]"
      style={{ 
        backgroundColor: isSubmitting ? `${COLORS.accent}66` : 'transparent',
        color: COLORS.accent,
        borderColor: COLORS.accent
      }}
      onMouseEnter={(e) => { if (!isSubmitting) { e.currentTarget.style.backgroundColor = `${COLORS.accent}22`; }}}
      onMouseLeave={(e) => { if (!isSubmitting) { e.currentTarget.style.backgroundColor = 'transparent'; }}}
    >
      {isSubmitting ? loadingLabel : label}
    </button>
  );

  return (
    <section
      ref={rsvpSectionRef}
      className="py-20 sm:py-24 relative overflow-hidden z-[1]"
      style={{ backgroundColor: COLORS.primary }}
    >
      <div className="px-5 sm:px-6 relative z-[2]">

        {/* Decorative divider */}
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="h-px w-12 sm:w-16" style={{ backgroundColor: `${COLORS.accent}66` }} />
          <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: COLORS.accent }} />
          <div className="h-px w-12 sm:w-16" style={{ backgroundColor: `${COLORS.accent}66` }} />
        </div>

        {/* Header */}
        <div className="text-center mb-10">
          <h3 className="font-glamour text-3xl font-bold mb-1" style={{ color: COLORS.secondary }}>
            RSVP & Wishes
          </h3>
          <p className="font-optima text-sm" style={{ color: `${COLORS.secondary}99` }}>
            {RSVP_CLOSED ? (
              'RSVP submissions are now closed'
            ) : (
              <>Kindly respond before <span className="font-semibold" style={{ color: COLORS.accent }}>{RSVP_DEADLINE}</span></>
            )}
          </p>
        </div>

        {/* Tabs */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative flex rounded-full" style={{ backgroundColor: `${COLORS.accent}1A` }}>
            <motion.div
              className="absolute h-full rounded-full"
              style={{ backgroundColor: COLORS.accent }}
              initial={false}
              animate={{ left: activeTab === 'rsvp' ? '0%' : '50%', width: '50%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            />
            {(['rsvp', 'wishes'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="relative flex-1 py-3 rounded-full text-sm font-optima z-10 transition-colors duration-300 tracking-[0.1em] uppercase"
                style={{ color: activeTab === tab ? COLORS.primary : `${COLORS.accent}DD` }}
              >
                {tab === 'rsvp' ? 'RSVP' : 'Send Wishes'}
              </button>
            ))}
          </div>
        </div>

        {/* Forms */}
        <div className="max-w-md mx-auto">
          <AnimatePresence mode="wait">

            {/* RSVP Tab */}
            {activeTab === 'rsvp' && (
              <motion.div
                key="rsvp"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {rsvpSubmitted ? (
                  <div className="py-14 text-center space-y-6">
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <div className="h-px w-10" style={{ backgroundColor: `${COLORS.accent}66` }} />
                      <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: COLORS.accent }} />
                      <div className="h-px w-10" style={{ backgroundColor: `${COLORS.accent}66` }} />
                    </div>
                    <div className="w-16 h-16 rounded-full border-2 flex items-center justify-center mx-auto" style={{ borderColor: COLORS.accent }}>
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: COLORS.accent }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="font-glamour text-2xl sm:text-3xl" style={{ color: COLORS.accent }}>Thank You!</h4>
                    <p className="font-optima text-sm leading-relaxed max-w-xs mx-auto" style={{ color: `${COLORS.accent}CC` }}>
                      Your attendance has been confirmed. We look forward to celebrating with you.
                    </p>
                  </div>
                ) : RSVP_CLOSED ? (
                  <div className="py-14 text-center space-y-6">
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <div className="h-px w-10" style={{ backgroundColor: `${COLORS.accent}66` }} />
                      <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: COLORS.accent }} />
                      <div className="h-px w-10" style={{ backgroundColor: `${COLORS.accent}66` }} />
                    </div>
                    <h4 className="font-glamour text-2xl sm:text-3xl" style={{ color: COLORS.accent }}>RSVP Closed</h4>
                    <p className="font-optima text-sm leading-relaxed max-w-xs mx-auto" style={{ color: `${COLORS.accent}CC` }}>
                      We are no longer accepting new RSVPs. Feel free to leave your wishes for the couple below.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-9">

                    {/* Name */}
                    <input
                      type="text"
                      required
                      placeholder="Your Name"
                      className={inputClass}
                      style={{ borderColor: borderDefault, color: COLORS.accent }}
                      onFocus={(e) => (e.target.style.borderColor = COLORS.accent)}
                      onBlur={(e) => (e.target.style.borderColor = borderDefault)}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />

                    {/* Phone */}
                    <input
                      type="tel"
                      required
                      placeholder="Phone Number"
                      className={inputClass}
                      style={{ borderColor: borderDefault, color: COLORS.accent }}
                      onFocus={(e) => (e.target.style.borderColor = COLORS.accent)}
                      onBlur={(e) => {
                        e.target.style.borderColor = borderDefault;
                        const formatted = formatPhoneNumber(e.target.value);
                        if (formatted !== e.target.value) setFormData({ ...formData, phone: formatted });
                      }}
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />

                    {/* Organization */}
                    <div className="relative">
                      <select
                        required
                        className={selectClass}
                        style={{
                          backgroundColor: COLORS.primary,
                          borderColor: borderDefault,
                          color: formData.organization ? COLORS.accent : `${COLORS.accent}99`,
                        }}
                        onFocus={(e) => (e.target.style.borderColor = COLORS.accent)}
                        onBlur={(e) => (e.target.style.borderColor = borderDefault)}
                        value={formData.organization}
                        onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      >
                        <option value="" style={{ backgroundColor: COLORS.primary, color: `${COLORS.accent}99` }}>Organization / Group</option>
                        {ORGANIZATION_OPTIONS.map((o) => (
                          <option key={o} value={o} style={{ backgroundColor: COLORS.primary, color: COLORS.accent }}>{o}</option>
                        ))}
                      </select>
                      <SelectArrow />
                    </div>

                    {/* Invitation From */}
                    <div className="relative">
                      <select
                        required
                        className={selectClass}
                        style={{
                          backgroundColor: COLORS.primary,
                          borderColor: borderDefault,
                          color: formData.invitationFrom ? COLORS.accent : `${COLORS.accent}99`,
                        }}
                        onFocus={(e) => (e.target.style.borderColor = COLORS.accent)}
                        onBlur={(e) => (e.target.style.borderColor = borderDefault)}
                        value={formData.invitationFrom}
                        onChange={(e) => setFormData({ ...formData, invitationFrom: e.target.value })}
                      >
                        <option value="" style={{ backgroundColor: COLORS.primary, color: `${COLORS.accent}99` }}>Invitation From</option>
                        {INVITATION_FROM_OPTIONS.map((o) => (
                          <option key={o} value={o} style={{ backgroundColor: COLORS.primary, color: COLORS.accent }}>{o}</option>
                        ))}
                      </select>
                      <SelectArrow />
                    </div>

                    {/* Number of Pax */}
                    <div className="relative">
                      <select
                        required
                        className={selectClass}
                        style={{
                          backgroundColor: COLORS.primary,
                          borderColor: borderDefault,
                          color: formData.guests ? COLORS.accent : `${COLORS.accent}99`,
                        }}
                        onFocus={(e) => (e.target.style.borderColor = COLORS.accent)}
                        onBlur={(e) => (e.target.style.borderColor = borderDefault)}
                        value={formData.guests || ''}
                        onChange={(e) => setFormData({ ...formData, guests: Number(e.target.value) })}
                      >
                        <option value="" style={{ backgroundColor: COLORS.primary, color: `${COLORS.accent}99` }}>Number of Pax</option>
                        {PAX_OPTIONS.map((n) => (
                          <option key={n} value={n} style={{ backgroundColor: COLORS.primary, color: COLORS.accent }}>
                            {n} {n === 1 ? 'person' : 'people'}
                          </option>
                        ))}
                      </select>
                      <SelectArrow />
                    </div>

                    {/* Wishes (optional) */}
                    <textarea
                      placeholder="Leave a wish for the couple (optional)"
                      rows={3}
                      className={`${inputClass} resize-none`}
                      style={{ borderColor: borderDefault, color: COLORS.accent }}
                      onFocus={(e) => (e.target.style.borderColor = COLORS.accent)}
                      onBlur={(e) => (e.target.style.borderColor = borderDefault)}
                      value={formData.wishes}
                      onChange={(e) => setFormData({ ...formData, wishes: e.target.value })}
                    />

                    {rsvpError && (
                      <p className="text-sm text-center" style={{ color: COLORS.error }}>{rsvpError}</p>
                    )}

                    <SubmitButton isSubmitting={rsvpSubmitting} label="Confirm Attendance" loadingLabel="Confirming..." />
                  </form>
                )}
              </motion.div>
            )}

            {/* Wishes Tab */}
            {activeTab === 'wishes' && (
              <motion.div
                key="wishes"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {wishesSubmitted ? (
                  <div className="py-14 text-center space-y-6">
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <div className="h-px w-10" style={{ backgroundColor: `${COLORS.accent}66` }} />
                      <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: COLORS.accent }} />
                      <div className="h-px w-10" style={{ backgroundColor: `${COLORS.accent}66` }} />
                    </div>
                    <div className="w-16 h-16 rounded-full border-2 flex items-center justify-center mx-auto" style={{ borderColor: COLORS.accent }}>
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: COLORS.accent }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="font-glamour text-2xl sm:text-3xl" style={{ color: COLORS.accent }}>Thank You!</h4>
                    <p className="font-optima text-sm leading-relaxed max-w-xs mx-auto" style={{ color: `${COLORS.accent}CC` }}>
                      Your wishes have been received. We are truly touched.
                    </p>
                  </div>
                ) : WISHES_CLOSED ? (
                  <div className="py-14 text-center space-y-6">
                    <div className="flex items-center justify-center gap-3 mb-6">
                      <div className="h-px w-10" style={{ backgroundColor: `${COLORS.accent}66` }} />
                      <div className="w-1.5 h-1.5 rotate-45" style={{ backgroundColor: COLORS.accent }} />
                      <div className="h-px w-10" style={{ backgroundColor: `${COLORS.accent}66` }} />
                    </div>
                    <h4 className="font-glamour text-2xl sm:text-3xl" style={{ color: COLORS.accent }}>Wishes Closed</h4>
                    <p className="font-optima text-sm leading-relaxed max-w-xs mx-auto" style={{ color: `${COLORS.accent}CC` }}>
                      We are no longer accepting new wishes. Thank you to everyone who shared their kind words.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleWishesSubmit} className="space-y-9">

                    {/* Name */}
                    <input
                      type="text"
                      required
                      placeholder="Your Name"
                      className={inputClass}
                      style={{ borderColor: borderDefault, color: COLORS.accent }}
                      onFocus={(e) => (e.target.style.borderColor = COLORS.accent)}
                      onBlur={(e) => (e.target.style.borderColor = borderDefault)}
                      value={wishesData.name}
                      onChange={(e) => setWishesData({ ...wishesData, name: e.target.value })}
                    />

                    {/* Message */}
                    <textarea
                      required
                      placeholder="Your Wishes"
                      rows={5}
                      className={`${inputClass} resize-none`}
                      style={{ borderColor: borderDefault, color: COLORS.accent }}
                      onFocus={(e) => (e.target.style.borderColor = COLORS.accent)}
                      onBlur={(e) => (e.target.style.borderColor = borderDefault)}
                      value={wishesData.message}
                      onChange={(e) => setWishesData({ ...wishesData, message: e.target.value })}
                    />

                    {wishesError && (
                      <p className="text-sm text-center" style={{ color: COLORS.error }}>{wishesError}</p>
                    )}

                    <SubmitButton isSubmitting={wishesSubmitting} label="Send Wishes" loadingLabel="Sending..." />
                  </form>
                )}
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default RSVPSection;
